import {
  evaluateAst,
  extractReferencesFromAst,
  extractWorkflowExpressionReferences,
  parseWorkflowExpression,
  type WorkflowExpressionAst,
  type WorkflowExpressionStep,
} from "./expressions";
import {
  isFlowSocket,
  isPotentiallyEmptyOutput,
  SOCKET_NOT_TAKEN,
} from "./types";

/**
 * Producer-side materialization of socket expressions.
 *
 * These helpers are the pure, deterministic core of the "combine socket
 * expressions and lambdas" design (see docs/combine-socket-expressions-and-lambdas.md).
 * They are safe to run inside the Temporal workflow sandbox — no DB, no I/O,
 * no clock/randomness. The impure wiring (persisting results, patching the VM's
 * command sequence) lives in backend/src/workflows/temporal and is layered on top.
 *
 * Model, keyed by the expression string:
 *   - A *bare* `(output "step" "socket")` reads the producer's raw output
 *     directly (`stepRun.outputs[socket]`) — nothing is materialized for it.
 *   - Any *transform* expression (e.g. `(concat …)`) is evaluated once its
 *     referenced producers have all landed (or died), and the value is stored
 *     under the expression string. Many consumers of an identical expression
 *     share the single stored result.
 */

/** True when the whole expression is a single bare `(output "s" "k")` read. */
export function isBareOutputExpression(
  ast: WorkflowExpressionAst,
): ast is WorkflowExpressionAst & { kind: "call"; name: "output" } {
  return ast.kind === "call" && ast.name === "output";
}

/**
 * Distinct transform expressions across every input socket. Bare `output`
 * reads are excluded — they resolve from raw outputs, not the store. Dedupes
 * identical expression strings (the free-sharing property).
 */
export function collectTransformExpressions(
  steps: WorkflowExpressionStep[],
): string[] {
  const seen = new Set<string>();
  for (const step of steps) {
    for (const socket of step.inputSockets) {
      if (!socket.expression) continue;
      if (seen.has(socket.expression)) continue;
      if (isBareOutputExpression(parseWorkflowExpression(socket.expression))) {
        continue;
      }
      seen.add(socket.expression);
    }
  }
  return [...seen];
}

/**
 * Evaluate every transform expression whose referenced producers have all
 * landed (present in `completedSteps`) or died (present in `deadSteps`),
 * returning a map keyed by expression string → value.
 *
 * A cross-producer expression (e.g. `(concat (output "a" …) (output "b" …))`)
 * only materializes once BOTH `a` and `b` are resolved, exactly like a lambda
 * fed by two steps. Expressions whose producers have not all resolved are
 * skipped this pass; call again as more producers land (monotonic — the set of
 * ready expressions only grows).
 *
 * Pass `alreadyMaterialized` (the run-level store built across iterations) to
 * skip any expression already computed. This is not merely an optimization: an
 * expression's value is final once its producers have landed, and under the
 * omit-raw optimization those producers' raw sockets are subsequently deleted
 * from `completedSteps` — re-evaluating a stored expression would then read
 * `undefined` and throw. Skipping keeps materialization monotonic and safe.
 */
export function materializeReadyExpressions(
  steps: WorkflowExpressionStep[],
  completedSteps: Map<string, Record<string, unknown>>,
  deadSteps?: Set<string>,
  alreadyMaterialized?: ReadonlyMap<string, unknown>,
): Map<string, unknown> {
  const context: Record<string, Record<string, unknown>> = Object.fromEntries(
    completedSteps,
  );
  const results = new Map<string, unknown>();

  for (const expression of collectTransformExpressions(steps)) {
    // Already computed: its value is final, and its raw inputs may since have
    // been omitted, so never re-evaluate it.
    if (alreadyMaterialized?.has(expression)) continue;
    const ast = parseWorkflowExpression(expression);
    const refs = extractReferencesFromAst(ast);
    const allResolved = refs.every(
      (ref) => completedSteps.has(ref.stepId) || deadSteps?.has(ref.stepId),
    );
    if (!allResolved) continue;
    // A dead producer must present the dead-branch sentinel, not `undefined`:
    // concat already drops undefined, but a transform (e.g. string_join) would
    // fail Zod arg validation and throw, aborting the whole pass. Seeding
    // SOCKET_NOT_TAKEN lets the evaluator's existing dead-branch propagation
    // return the sentinel for the expression instead.
    for (const ref of refs) {
      if (completedSteps.has(ref.stepId)) continue;
      if (!deadSteps?.has(ref.stepId)) continue;
      context[ref.stepId] ??= {};
      context[ref.stepId][ref.socketId] = SOCKET_NOT_TAKEN;
    }
    // This runs the instant every producer *step* lands — before we know
    // whether the expression's actual consumer(s) are reachable. A live
    // producer can legitimately leave one referenced output socket absent
    // (an optional socket it chose not to populate — not a dead branch, so
    // it never gets the SOCKET_NOT_TAKEN substitution above), which a strict
    // transform like string_join rejects. If the only consumer(s) of this
    // expression are themselves dead/unreachable, that failure is pure noise:
    // nothing will ever read the value. Skip it instead of throwing —
    // foldDerivedExpressions already treats a missing store entry as "not
    // ready yet" and keeps the raws, and resolveExpressionInput's inline
    // fallback re-evaluates (and surfaces any real error) exactly when a
    // reachable consumer actually needs the value.
    try {
      results.set(expression, evaluateAst(ast, context));
    } catch {
      continue;
    }
  }

  return results;
}

/**
 * The omit-raw optimization, computed statically from the (pinned) definition.
 *
 * Returns the set of producer output sockets — keyed `${stepId}:${socketId}` —
 * whose raw value can be dropped because every downstream reference wraps it in
 * a transform, so the raw is never read directly. An output with a bare
 * `(output …)` consumer, or with NO downstream consumer at all (a terminal /
 * observability output), is kept.
 *
 * Safety: only sound relative to the definition version these expressions came
 * from. A later edit that adds a new bare/transform reference over an omitted
 * output must re-materialize — the caller owns that guard (open Q4).
 */
export function computeOmittableOutputs(
  steps: WorkflowExpressionStep[],
): Set<string> {
  const referencedAny = new Set<string>();
  const referencedBare = new Set<string>();

  for (const step of steps) {
    for (const socket of step.inputSockets) {
      if (!socket.expression) continue;
      const ast = parseWorkflowExpression(socket.expression);
      const bare = isBareOutputExpression(ast);
      for (const ref of extractReferencesFromAst(ast)) {
        const key = `${ref.stepId}:${ref.socketId}`;
        referencedAny.add(key);
        if (bare) referencedBare.add(key);
      }
    }
  }

  for (const key of referencedBare) referencedAny.delete(key);
  return referencedAny;
}

/**
 * The sole producer step referenced by an expression, or `null` when it
 * references zero producers or more than one. A "single-producer" transform is
 * one whose result is fully determined the moment that one producer lands — so
 * it can be materialized onto (and persisted with) that producer's output
 * record, unambiguously. Cross-producer transforms have no single home.
 */
export function expressionSoleProducer(expression: string): string | null {
  const producers = new Set(
    extractWorkflowExpressionReferences(expression).map((ref) => ref.stepId),
  );
  return producers.size === 1 ? [...producers][0] : null;
}

export interface ExpressionOmissionPlan {
  /**
   * Raw output sockets (`${stepId}:${socketId}`) safe to drop from the
   * producer's output record: read only through single-producer transforms over
   * that same producer, never bare and never by a cross-producer transform.
   */
  omittableSockets: Set<string>;
  /**
   * Per producer step id, the single-producer transform expressions that read
   * at least one of its omitted sockets — the values that must be materialized
   * onto that producer so the omitted raws stay re-derivable.
   */
  derivedByProducer: Map<string, string[]>;
}

/**
 * Plan the omit-raw optimization under the single-producer safety rule.
 *
 * Only single-producer transforms are eligible: their result attaches
 * unambiguously to the one producer that feeds them, so dropping that
 * producer's raw socket is safe as long as the derived value travels with it
 * (in `completedSteps[producer]`, persisted on its StepRun, decomposed into
 * fanout children — all the existing plumbing). A socket read bare, or read by
 * any cross-producer transform, is always kept and re-derived (see
 * {@link materializeReadyExpressions}).
 *
 * `edges` (the definition's stored edges) guards mixed migration states: a
 * producer socket still consumed through a legacy, non-expression data edge is
 * kept, because that consumer reads it via the edge path — invisible to this
 * expression-only analysis. Omit `edges` only when every consumer is known to
 * be expression-backed.
 */
export function planExpressionOmission(
  steps: WorkflowExpressionStep[],
  edges?: ReadonlyArray<{
    sourceStepId: string;
    sourceSocketId: string;
    targetStepId: string;
    targetSocketId: string;
  }>,
): ExpressionOmissionPlan {
  const bareRead = new Set<string>();
  const crossProducerRead = new Set<string>();
  const singleProducerRead = new Set<string>();
  // expression -> its sole producer, for single-producer transforms only.
  const soleProducerByExpression = new Map<string, string>();

  for (const step of steps) {
    for (const socket of step.inputSockets) {
      if (!socket.expression) continue;
      const ast = parseWorkflowExpression(socket.expression);
      const refs = extractWorkflowExpressionReferences(socket.expression);

      if (isBareOutputExpression(ast)) {
        for (const ref of refs) bareRead.add(`${ref.stepId}:${ref.socketId}`);
        continue;
      }

      const sole = expressionSoleProducer(socket.expression);
      if (sole === null) {
        for (const ref of refs) {
          crossProducerRead.add(`${ref.stepId}:${ref.socketId}`);
        }
        continue;
      }

      soleProducerByExpression.set(socket.expression, sole);
      for (const ref of refs) {
        singleProducerRead.add(`${ref.stepId}:${ref.socketId}`);
      }
    }
  }

  // A producer socket still read through a legacy (stored, non-expression) data
  // edge must be kept: a not-yet-migrated consumer reads it via the edge path,
  // which this expression-only analysis cannot see. Deleting it would starve
  // that consumer. Dual-write sockets (edge AND expression) are NOT legacy —
  // they resolve via the expression and are accounted for above.
  const socketHasExpression = new Map<string, boolean>();
  for (const step of steps) {
    for (const socket of step.inputSockets) {
      socketHasExpression.set(`${step.id}:${socket.id}`, !!socket.expression);
    }
  }
  const legacyEdgeRead = new Set<string>();
  for (const edge of edges ?? []) {
    if (
      isFlowSocket(edge.sourceSocketId) ||
      isFlowSocket(edge.targetSocketId)
    ) {
      continue;
    }
    // No expression on the target ⇒ it consumes the source via the edge path.
    // A target not declared in `steps` is treated as legacy too (keep, be safe).
    const key = `${edge.targetStepId}:${edge.targetSocketId}`;
    if (!socketHasExpression.get(key)) {
      legacyEdgeRead.add(`${edge.sourceStepId}:${edge.sourceSocketId}`);
    }
  }

  const omittableSockets = new Set<string>();
  for (const key of singleProducerRead) {
    if (
      !bareRead.has(key) &&
      !crossProducerRead.has(key) &&
      !legacyEdgeRead.has(key)
    ) {
      omittableSockets.add(key);
    }
  }

  // A derived expression is worth persisting only when it reads an omitted
  // socket — otherwise its inputs survive as raws and it re-derives for free.
  const derivedByProducer = new Map<string, string[]>();
  for (const [expression, producer] of soleProducerByExpression) {
    const touchesOmitted = extractWorkflowExpressionReferences(expression).some(
      (ref) => omittableSockets.has(`${ref.stepId}:${ref.socketId}`),
    );
    if (!touchesOmitted) continue;
    const existing = derivedByProducer.get(producer);
    if (existing) {
      if (!existing.includes(expression)) existing.push(expression);
    } else {
      derivedByProducer.set(producer, [expression]);
    }
  }

  return { omittableSockets, derivedByProducer };
}

/**
 * Apply the omit-raw plan to the in-VM `completedSteps`: for every producer
 * that has landed, fold its derived transform results (taken from
 * `expressionStore`) onto its own output record, then delete its omittable raw
 * sockets. Idempotent — safe to call every iteration. Mutates `completedSteps`.
 *
 * This is the step that shrinks the payload: `buildParentOutputs` decomposes
 * `completedSteps` into every fanout child's input, so dropping a big raw
 * column that is only read through a transform keeps it out of each child (and
 * out of ongoing workflow history). A producer's raws are dropped only once all
 * of its derived expressions are folded, so the folded value is always there to
 * resolve from.
 *
 * ⚠️ Gate the call behind `workflow.patched()`: changing `completedSteps`
 * changes child-workflow inputs, which is a command-sequence change for
 * in-flight runs.
 */
export function foldDerivedExpressions(
  completedSteps: Map<string, Record<string, unknown>>,
  expressionStore: Map<string, unknown>,
  plan: ExpressionOmissionPlan,
): void {
  for (const [producer, expressions] of plan.derivedByProducer) {
    const record = completedSteps.get(producer);
    if (!record) continue; // producer has not landed yet

    let allFolded = true;
    for (const expression of expressions) {
      if (expression in record) continue;
      if (expressionStore.has(expression)) {
        record[expression] = expressionStore.get(expression);
      } else {
        allFolded = false; // value not ready — keep the raws it covers
      }
    }
    if (!allFolded) continue;

    for (const key of plan.omittableSockets) {
      const separator = key.indexOf(":");
      const stepId = key.slice(0, separator);
      const socketId = key.slice(separator + 1);
      if (stepId !== producer || !(socketId in record)) continue;
      // Never drop a dead-branch marker (SOCKET_NOT_TAKEN or empty array):
      // isEdgeEmpty inspects the producer's raw socket to propagate pruning,
      // and an absent socket reads as "not empty", which would wrongly keep a
      // dead branch alive. These markers are tiny, so keeping them costs
      // nothing — only real payloads are worth trimming.
      if (isPotentiallyEmptyOutput(record[socketId])) continue;
      delete record[socketId];
    }
  }
}

/**
 * Consumer-side resolution of one input socket's expression.
 *
 * Bare `(output "s" "k")` reads the raw output from `completedSteps` directly
 * (the output-only path); any transform expression is looked up in the
 * materialized `expressionStore`, falling back to direct evaluation if the
 * store lacks the key (e.g. store not yet populated) so behaviour degrades to
 * today's consumer-side eval rather than dropping the input.
 */
export function resolveExpressionInput(
  expression: string,
  completedSteps: Map<string, Record<string, unknown>>,
  expressionStore: Map<string, unknown>,
): unknown {
  const ast = parseWorkflowExpression(expression);
  if (isBareOutputExpression(ast)) {
    const [stepArg, socketArg] = ast.args;
    if (stepArg.kind === "string" && socketArg.kind === "string") {
      return completedSteps.get(stepArg.value)?.[socketArg.value];
    }
  }
  // A single-producer transform whose value was folded onto its producer's
  // output record (the omit-raw path) resolves from there first: that folded
  // value is the copy that survives completedSteps trimming, fanout
  // decomposition into children, and rerun reseeding — none of which the
  // run-level store rides. Falls back to the store, then inline eval, so the
  // no-omit path behaves exactly as before.
  const soleProducer = expressionSoleProducer(expression);
  if (soleProducer !== null) {
    const record = completedSteps.get(soleProducer);
    if (record && expression in record) {
      return record[expression];
    }
  }
  if (expressionStore.has(expression)) {
    return expressionStore.get(expression);
  }
  return evaluateAst(ast, Object.fromEntries(completedSteps));
}
